import { useState, type CSSProperties, type MouseEvent as ReactMouseEvent } from 'react'
import { cancelOrder } from '../api'
import { price, qty } from '../format'
import { OrderEditPopover } from './OrderEditPopover'
import { QuickSweepPopover } from './QuickSweepPopover'
import type { Ladder, LadderRow } from '../ladder'
import type { ShadowOrder, Side } from '../types'

/**
 * Emir defteri tablosu — aynalı düzen:
 *
 *     K.Miktar │ Miktar │ Alış  ‖ Satış │ Miktar │ K.Miktar
 *
 * `K.Miktar` o seviyeye kadar biriken toplam miktardır (kümülatif derinlik):
 * "bu fiyata kadar süpürürsem toplam ne kadar alırım/satarım".
 *
 * Fiyat hücrelerindeki çubuk **kümülatif** miktarı gösterir, seviyenin kendi
 * miktarını değil — merkezden dışa doğru büyür, iki taraf ortak ölçekle
 * boyanır ki hangi tarafın daha derin olduğu görünsün.
 *
 * **Kümülatif ve çubuk ölçeği defterin TAMAMI üzerinden** hesaplanır, gösterilen
 * sayfa üzerinden değil: 2. sayfa 1. sayfanın toplamından devam eder, ölçek
 * sabit kaldığı için çubuklar sayfalar arası karşılaştırılabilir.
 *
 * Panel kabuğunu ve sayfalayıcıyı `DepthPanel` tutar; burası yalnız gövde.
 *
 * Veri kaynağı `depth2` (seviye başına emir kırılımı yalnız orada var).
 *
 * **Kendi gölge emirlerimiz**: miktar kendi sütununda mor, fiyat da mor, satırın
 * o yarısı mor bantla çevrili (`--owner`, eşleşme listesindeki gölge dolum
 * işaretiyle aynı dil). Miktarımız gerçek miktara ve kümülatife **eklenmez** —
 * gerekçe `ladder.ts` başlığında. Emrimiz gerçek bir seviyeye denk gelmiyorsa
 * (makasın içi) o fiyat için sentetik satır açılır; kümülatif `–` görünür,
 * çünkü o fiyata kadar süpürecek gerçek derinlik yok.
 *
 * **K.Miktar hücresi aynı zamanda eylem yeri**: kendi emrimizin olduğu satırda
 * düzenle (✎) ve sil (🗑) ikonları çıkar, defterden çıkmadan müdahale edilir.
 * Düzenleme modal değil, hücrenin yanında açılan balon (`OrderEditPopover`).
 *
 * Organizasyonun GERÇEK emirleri işaretlenmiyor: bu projede gerçek pozisyonu
 * kullanmıyoruz.
 */

interface Props {
  ladder: Ladder
  hasDepth: boolean
  page: number
  rowsPerPage: number
  contract: string | null
  closed: boolean
}

/** Açık Hızlı Al/Sat balonunun durumu. */
interface SweepState {
  side: Side
  price: number
  lots: number
  at: { x: number; y: number }
}

export function DepthLadder({ ladder, hasDepth, page, rowsPerPage, contract, closed }: Props) {
  // Açık düzenleme balonu: hangi emir ve hangi hücrenin yanında.
  const [duzenlenen, setDuzenlenen] = useState<{ order: ShadowOrder; anchor: DOMRect } | null>(null)
  const [supurme, setSupurme] = useState<SweepState | null>(null)
  const [hata, setHata] = useState<string | null>(null)

  if (!hasDepth) {
    return <div className="tab-empty">Derinlik bekleniyor…</div>
  }

  const start = page * rowsPerPage
  const end = Math.min(start + rowsPerPage, ladder.rowCount)

  /** Seviyedeki emirlerin hepsini iptal et. "Sil" kaydı silmez, iptal eder. */
  async function sil(orders: ShadowOrder[]) {
    setHata(null)
    try {
      await Promise.all(orders.map((o) => cancelOrder(o.id)))
    } catch (err) {
      setHata(err instanceof Error ? err.message : 'Emir iptal edilemedi')
    }
  }

  function duzenle(orders: ShadowOrder[], anchor: DOMRect) {
    setHata(null)
    // Aynı fiyattan birden çok emrimiz varsa hangisinin düzenleneceği belirsiz;
    // o durumda kullanıcıyı Emirlerim sekmesine yönlendiriyoruz.
    const tek = orders.length === 1 ? orders[0] : undefined
    if (!tek) {
      setHata(`Bu seviyede ${orders.length} emir var — Emirlerim sekmesinden düzenle.`)
      return
    }
    setDuzenlenen({ order: tek, anchor })
  }

  /**
   * Fiyata tıklama → Hızlı Al/Sat balonu. Satış seviyesine tıklamak ALIŞ
   * süpürmesidir (o ve daha ucuz satışların hepsi), alış seviyesine tıklamak
   * SATIŞ. Gösterilen lot, o satıra kadarki `available` toplamı — önizleme;
   * kesin miktarı sunucu kilit altında hesaplar.
   */
  function hizliAc(bookSide: 'bid' | 'ask', index: number, ev: ReactMouseEvent) {
    if (closed || !contract) return
    setHata(null)
    const rows = bookSide === 'bid' ? ladder.bids : ladder.asks
    const tiklanan = rows[index]
    if (!tiklanan) return
    const lots = rows
      .slice(0, index + 1)
      .reduce((toplam, r) => toplam + r.available, 0)
    if (lots <= 0) return           // her şey süpürülmüş — balon açmanın anlamı yok
    setSupurme({
      side: bookSide === 'ask' ? 'BUY' : 'SELL',
      price: tiklanan.price,
      lots,
      at: { x: ev.clientX, y: ev.clientY },
    })
  }

  return (
    <>
      <div className="ladder-head">
        <span>K.Miktar</span>
        <span>Miktar</span>
        <span>Alış</span>
        <span>Satış</span>
        <span>Miktar</span>
        <span>K.Miktar</span>
      </div>

      {/* Satır yüksekliği, gösterilen satır sayısına değil SABİT sayfa boyutuna
          bölünmüş alana bağlı: 10 satır paneli tam doldurur, son sayfada 4 satır
          kalırsa satırlar şişmez, altta boşluk kalır. */}
      <div className="ladder" style={{ '--ladder-rows': rowsPerPage } as CSSProperties}>
        {Array.from({ length: Math.max(0, end - start) }, (_, offset) => {
          const i = start + offset
          const bid = ladder.bids[i]
          const ask = ladder.asks[i]
          // Vurgu bandı emrin bulunduğu TARAFIN üç hücresini kaplar. Karşı taraf
          // dışarıda: aynalı defterde satırın iki yarısı ilgisiz iki fiyat
          // seviyesidir, altısını birden boyamak karşı tarafı da bizimmiş gibi
          // gösterirdi. Bandın dış uçlarına `start`/`end` veriliyor ki kenar
          // çizgisi ve yuvarlak köşe yalnız uçlarda çıksın.
          return (
            <div className="ladder-row" key={i}>
              <QuantityCell
                row={bid} field="cumulative" muted band="start"
                onEdit={duzenle} onDelete={sil}
              />
              <QuantityCell row={bid} field="quantity" />
              <PriceCell row={bid} side="bid" scale={ladder.scale} band="end" index={i} onQuick={hizliAc} />
              <PriceCell row={ask} side="ask" scale={ladder.scale} band="start" index={i} onQuick={hizliAc} />
              <QuantityCell row={ask} field="quantity" />
              <QuantityCell
                row={ask} field="cumulative" muted band="end"
                onEdit={duzenle} onDelete={sil}
              />
            </div>
          )
        })}
      </div>

      {hata && <div className="ladder-error" role="alert">{hata}</div>}

      {duzenlenen && (
        <OrderEditPopover
          order={duzenlenen.order}
          anchor={duzenlenen.anchor}
          onClose={() => setDuzenlenen(null)}
        />
      )}

      {supurme && contract && (
        <QuickSweepPopover
          contract={contract}
          side={supurme.side}
          price={supurme.price}
          lots={supurme.lots}
          at={supurme.at}
          onClose={() => setSupurme(null)}
        />
      )}
    </>
  )
}

/**
 * Miktar / kümülatif hücresi.
 *
 * **Miktar:** kendi gölge miktarımız kendi sütununda, mor. Gerçek derinliğin de
 * olduğu bir seviyedeysek iki sayı yan yana durur (gerçek + mor bizimki);
 * toplanmazlar, çünkü emrimiz piyasada yok.
 *
 * **K.Miktar:** sentetik satırda `–`. O fiyata kadar süpürecek gerçek derinlik
 * yok; `0` yazmak "sıfır lot var" gibi okunurdu. Kendi miktarımız buraya
 * hiçbir koşulda girmez — kendi emrimizle işlem yapamayız.
 */
function QuantityCell({
  row,
  field,
  muted = false,
  band,
  onEdit,
  onDelete,
}: {
  row: LadderRow | undefined
  field: 'quantity' | 'cumulative'
  muted?: boolean
  band?: 'start' | 'end'
  onEdit?: (orders: ShadowOrder[], anchor: DOMRect) => void
  onDelete?: (orders: ShadowOrder[]) => void
}) {
  const sinif =
    `ladder-cell${muted ? ' muted' : ''}` +
    (row?.mine ? ` mine${band ? ` band-${band}` : ''}` : '')
  if (!row) return <span className={`ladder-cell${muted ? ' muted' : ''}`} />

  if (field === 'cumulative') {
    // Kendi emrimizin olduğu satırda kümülatif hücresi aynı zamanda eylem
    // yeri: düzenle + sil. Defterden çıkmadan müdahale edilebilsin diye.
    if (row.mine > 0 && onEdit && onDelete) {
      const kacEmir = row.orders.length
      const tekEmir = kacEmir === 1 ? row.orders[0] : undefined
      return (
        <span className={`${sinif} has-actions`}>
          <span className="cum-value">
            {row.synthetic ? <span className="placeholder">–</span> : qty(row.cumulative)}
          </span>
          <button
            type="button"
            className="icon-btn tiny"
            title={
              tekEmir ? `${tekEmir.id} — düzenle` : `Bu seviyede ${kacEmir} emir var`
            }
            aria-label="Emri düzenle"
            onClick={(e) => onEdit(row.orders, e.currentTarget.getBoundingClientRect())}
          >
            ✎
          </button>
          <button
            type="button"
            className="icon-btn tiny danger"
            title={
              tekEmir
                ? `${tekEmir.id} — sil (kayıt silinmez, iptal edilir)`
                : `${kacEmir} emrin hepsini iptal et`
            }
            aria-label="Emri sil"
            onClick={() => onDelete(row.orders)}
          >
            🗑
          </button>
        </span>
      )
    }

    return row.synthetic ? (
      <span className={`${sinif} placeholder`}>–</span>
    ) : (
      <span className={sinif}>{qty(row.cumulative)}</span>
    )
  }

  // Yalnız bizim emrimizin açtığı satır: tek sayı, mor.
  if (row.synthetic) {
    return <span className={sinif}><span className="mine-qty">{qty(row.mine)}</span></span>
  }

  return (
    <span className={sinif}>
      {qty(row.quantity)}
      {row.mine > 0 && <span className="mine-qty">+{qty(row.mine)}</span>}
    </span>
  )
}

function PriceCell({
  row,
  side,
  scale,
  band,
  index,
  onQuick,
}: {
  row: LadderRow | undefined
  side: 'bid' | 'ask'
  scale: number
  band?: 'start' | 'end'
  index?: number
  onQuick?: (bookSide: 'bid' | 'ask', index: number, ev: ReactMouseEvent) => void
}) {
  if (!row) return <span className={`ladder-cell ${side}`} />

  // Süpürüldü: gerçek seviye tahtada duruyor ama tamamını biz tükettik —
  // gölge işlemde gerçekten almadığımız için piyasadan düşmez, ama bize
  // kapalıdır. İşaretlenir ve tıklanamaz.
  const supuruldu = !row.synthetic && row.quantity > 0 && row.available <= 0
  const tiklanir = onQuick != null && index != null && !supuruldu && !row.synthetic && row.quantity > 0

  const ipucu = supuruldu
    ? 'Süpürüldü — bu seviye artık size kapalı (tahtada durur, gölge işlem piyasadan düşürmez)'
    : row.mine
      ? `Gölge emrimiz: ${qty(row.mine)} lot @ ${price(row.price)}` +
        (row.synthetic
          ? ' · piyasada bu fiyatta seviye yok'
          : ` · piyasada bu seviyede ${qty(row.quantity)} lot (bizimki dahil değil)`)
      : tiklanir
        ? `${price(row.price)} — Hızlı ${side === 'ask' ? 'Al' : 'Sat'} için tıkla`
        : undefined

  const sinif =
    `ladder-cell ${side}` +
    (row.mine ? ` mine${band ? ` band-${band}` : ''}` : '') +
    (supuruldu ? ' swept' : tiklanir ? ' sweepable' : '')

  return (
    <span
      className={sinif}
      title={ipucu}
      onClick={tiklanir ? (ev) => onQuick(side, index, ev) : undefined}
    >
      <span className={`bar ${side}-bar`} style={{ width: barWidth(row.cumulative, scale) }} />
      {/* Fiyat da mor: satırın bize ait olduğu tek bakışta görünsün. */}
      <span className={`cell-text${row.mine ? ' mine' : ''}`}>
        {price(row.price)}
        {supuruldu && <span className="swept-mark" aria-label="süpürüldü">⊘</span>}
      </span>
    </span>
  )
}

function barWidth(cumulativeQty: number | undefined, scale: number): string {
  return cumulativeQty == null ? '0%' : `${Math.max(2, (cumulativeQty / scale) * 100)}%`
}
